import type { CookieJar } from '@hyphengroup/cms-components/utils/cookies';
import { parseScaleoClickParams } from '@hyphengroup/cms-components/utils/sessionAttributionTracking/scaleoClickParams';
import {
  ATTRIBUTION_COOKIE_NAME,
  readSessionScaleoParams,
} from '@hyphengroup/cms-components/utils/sessionAttributionTracking/sessionAttributionTracker';
import { getAllTrackingParams } from '@hyphengroup/cms-components/utils/tracking';
import { MembershipTracking } from '__generated__/graphql';
import { MembershipRewardsTrackingData } from 'utils/tracking';

export type TrackingParams = {
  clkid: string | null;
  clktype: string | null;
  utm_campaign: string | null;
  utm_medium: string | null;
  utm_source: string | null;
  utm_content: string | null;
  utm_term: string | null;
  utm_channel: string | null;
  utm_strategy: string | null;
  gclid: string | null;
};

/**
 * Read the Scaleo click pair for this visit: the current URL first, then the
 * session attribution record.
 *
 * Unlike the UTMs it never falls back to the `tracking` cookie, which outlives
 * the session — inheriting an affiliate click into a later organic visit had
 * OG Rewards book that lead as an affiliate conversion (CORE-4767). Both
 * sources are format-validated, so a malformed `clkid` yields a null pair
 * rather than being forwarded to the lead.
 */
function getScaleoParams(cookies: CookieJar, search: string) {
  return (
    parseScaleoClickParams(search).params ??
    readSessionScaleoParams(cookies.get(ATTRIBUTION_COOKIE_NAME)?.value) ?? {
      clkid: null,
      clktype: null,
    }
  );
}

/**
 * Typed subset of tracking params used across the app. Thin wrapper over
 * cms-components' `getAllTrackingParams`, so it shares the exact
 * URL-first-then-cookie behaviour and cannot diverge from what is persisted.
 * The Scaleo pair is the one exception — see `getScaleoParams`.
 */
export function getTrackingParams(
  cookies: CookieJar,
  search = '',
): TrackingParams {
  const all = getAllTrackingParams(cookies, search);
  const pick = (key: keyof TrackingParams): string | null => all[key] ?? null;
  const scaleo = getScaleoParams(cookies, search);

  return {
    clkid: scaleo.clkid,
    clktype: scaleo.clktype,
    utm_campaign: pick('utm_campaign'),
    utm_medium: pick('utm_medium'),
    utm_source: pick('utm_source'),
    utm_content: pick('utm_content'),
    utm_term: pick('utm_term'),
    utm_channel: pick('utm_channel'),
    utm_strategy: pick('utm_strategy'),
    gclid: pick('gclid'),
  };
}

export function createMembershipRewardsTracking(
  lead: MembershipTracking,
): MembershipRewardsTrackingData {
  return {
    email: lead.email ?? '',
    fullName: lead.fullName,
    contactNumber: lead.contactNumber,
    leadId: lead.leadId,
    campaignName: lead.product?.productName,
    campaignId: lead.productId,
    selectedReward: lead.offer?.offerName,
    product: {
      productUid: lead.product?.catalogueProduct?.productUid,
      productName: lead.product?.catalogueProduct?.productName,
      providerName: lead.product?.catalogueProduct?.provider?.providerName,
    },
  };
}
